import { useState, useEffect, useMemo } from 'react';
import { Button, TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

const BranchManager = ({ branches, onSave, onCancel, isSaving = false, isLoading = false }) => {
    const [searchTerm,    setSearchTerm]    = useState('');
    const [localBranches, setLocalBranches] = useState(branches);

    useEffect(() => { setLocalBranches(branches); }, [branches]);

    const stats = useMemo(() => {
        const active = localBranches.filter(b => b.IsActive).length;
        return { total: localBranches.length, active, inactive: localBranches.length - active };
    }, [localBranches]);

    const filteredBranches = useMemo(() =>
        localBranches
            .map((b, idx) => ({ ...b, originalIndex: idx }))
            .filter(b => !searchTerm || (b.BranchName || '').toLowerCase().includes(searchTerm.toLowerCase()))
    , [localBranches, searchTerm]);

    const handleToggle = (originalIndex) => {
        setLocalBranches(prev => {
            const updated = [...prev];
            updated[originalIndex] = { ...updated[originalIndex], IsActive: !updated[originalIndex].IsActive };
            return updated;
        });
    };

    const handleAllOn  = () => setLocalBranches(prev => prev.map(b => ({ ...b, IsActive: true  })));
    const handleAllOff = () => setLocalBranches(prev => prev.map(b => ({ ...b, IsActive: false })));
    const handleSave   = () => onSave(localBranches);

    return (
        <div style={{ background: 'white', display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'Times New Roman' }}>

            {/* ── Gradient Header ── */}
            <div style={{
                background: 'linear-gradient(135deg, #2C3E6E 0%, #3d5491 100%)',
                padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px',
            }}>
                <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: '7px', padding: '6px', display: 'flex' }}>
                    <StoreIcon style={{ fontSize: '18px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>Manage Login Branches</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)', marginTop: '1px' }}>
                        {stats.active}/{stats.total} branches allowed
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                    {[
                        { label: `${stats.total} Total`,      bg: 'rgba(255,255,255,0.18)' },
                        { label: `${stats.active} Allowed`,   bg: 'rgba(17,153,142,0.5)'   },
                        { label: `${stats.inactive} Restricted`, bg: 'rgba(245,87,108,0.4)' },
                    ].map(s => (
                        <span key={s.label} style={{ background: s.bg, color: 'white', padding: '3px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: '700' }}>
                            {s.label}
                        </span>
                    ))}
                </div>
                <IconButton onClick={onCancel} size="small" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <CloseIcon style={{ fontSize: '18px' }} />
                </IconButton>
            </div>

            {/* ── Search + All Off ── */}
            <div style={{ padding: '12px 16px 8px 16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <TextField
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search branch name..."
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: '16px', color: '#2C3E6E' }} /></InputAdornment>,
                        style: { fontSize: '12px', height: '32px' }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '7px', backgroundColor: '#f8f9fa', '&:hover fieldset': { borderColor: '#2C3E6E' }, '&.Mui-focused fieldset': { borderColor: '#2C3E6E' } } }}
                />
                <button
                    onClick={handleAllOff}
                    style={{
                        background: '#b71c1c', color: 'white', border: 'none',
                        borderRadius: '6px', padding: '5px 10px', fontSize: '10px',
                        fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
                        fontFamily: 'Times New Roman',
                    }}
                >
                    ✕ All Off
                </button>
            </div>

            {/* ── Matrix list ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px', minHeight: 0 }}>

                {/* Column headers */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', gap: '6px', padding: '0 8px 6px 8px', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Branch</div>
                    <div style={{ textAlign: 'center' }}>
                        <span style={{ background: '#2e7d32', color: 'white', padding: '2px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: '700' }}>Allow</span>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</div>
                </div>

                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <CircularProgress size={28} style={{ color: '#2C3E6E' }} />
                    </div>
                ) : filteredBranches.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#a0aec0', fontSize: '12px' }}>
                        {searchTerm ? `No branches match "${searchTerm}"` : 'No branches available'}
                    </div>
                ) : filteredBranches.map((branch, i) => (
                    <div
                        key={branch.BranchId}
                        onClick={() => handleToggle(branch.originalIndex)}
                        style={{
                            display: 'grid', gridTemplateColumns: '1fr 80px 80px',
                            gap: '6px', alignItems: 'center',
                            padding: '10px 8px', marginBottom: '5px', borderRadius: '8px',
                            background: branch.IsActive ? 'rgba(44,62,110,0.06)' : i % 2 === 0 ? '#fafafa' : 'white',
                            border: `1px solid ${branch.IsActive ? '#2C3E6E40' : '#f0f0f0'}`,
                            borderLeft: `3px solid ${branch.IsActive ? '#11998e' : '#e2e8f0'}`,
                            cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
                        }}
                    >
                        {/* Branch info */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '28px', height: '28px', borderRadius: '7px', flexShrink: 0,
                                background: branch.IsActive ? '#2C3E6E' : '#e2e8f0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.15s',
                            }}>
                                <StoreIcon style={{ fontSize: '14px', color: branch.IsActive ? 'white' : '#718096' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: branch.IsActive ? '700' : '500', color: branch.IsActive ? '#2C3E6E' : '#2d3748' }}>
                                    {branch.BranchName}
                                </div>
                                <div style={{ fontSize: '10px', color: '#a0aec0' }}>ID: {branch.BranchId}</div>
                            </div>
                        </div>

                        {/* Toggle checkbox */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{
                                width: '24px', height: '24px', borderRadius: '6px',
                                background: branch.IsActive ? '#11998e' : '#e2e8f0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                                boxShadow: branch.IsActive ? '0 2px 6px rgba(17,153,142,0.4)' : 'none',
                            }}>
                                {branch.IsActive && <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />}
                            </div>
                        </div>

                        {/* Status */}
                        <div style={{ textAlign: 'right' }}>
                            <span style={{
                                fontWeight: '700', fontSize: '11px',
                                color: branch.IsActive ? '#11998e' : '#a0aec0',
                            }}>
                                {branch.IsActive ? 'Allowed' : 'Restricted'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Footer ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #f0f0f0', gap: '8px' }}>
                <button
                    onClick={handleAllOn}
                    style={{
                        background: '#e8f5e9', color: '#2e7d32', border: 'none',
                        borderRadius: '6px', padding: '5px 12px', fontSize: '11px',
                        fontWeight: '700', cursor: 'pointer', fontFamily: 'Times New Roman',
                    }}
                >
                    ✓ All On
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button onClick={onCancel} disabled={isSaving} variant="outlined" size="small"
                        sx={{ borderColor: '#d0d7e3', color: '#4a5568', fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '7px', minHeight: '30px', textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving || isLoading} variant="contained" size="small"
                        startIcon={isSaving ? <CircularProgress size={11} style={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: '13px' }} />}
                        sx={{
                            background: 'linear-gradient(135deg,#2C3E6E,#3d5491)', color: 'white',
                            fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '7px',
                            minHeight: '30px', textTransform: 'none', boxShadow: '0 2px 6px rgba(44,62,110,0.3)',
                            '&:hover': { background: 'linear-gradient(135deg,#1a2a4a,#2C3E6E)' },
                        }}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BranchManager;
